import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  makeStyles,
  tokens,
  createTableColumn, DataGrid,
  DataGridBody, DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Menu, MenuItem, MenuList, MenuPopover, MenuTrigger,
  TableColumnDefinition
} from "@fluentui/react-components";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { DeleteRegular, EditRegular, MoreHorizontalFilled, AddRegular } from '@fluentui/react-icons';
import { uniqWith } from 'lodash-es';
import isEqual from 'lodash-es/isEqual';

const removeSimilarStrings = (arr: string[]) =>
  uniqWith(arr, (a, b) => a.toLowerCase().includes(b.toLowerCase()) || b.toLowerCase().includes(a.toLowerCase()));


const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalL,
  },
  actions: {
    marginTop: tokens.spacingVerticalL,
    maxWidth: '150px'
  }
});

export const AppFeaturesMEDomain: React.FC<{
  items: string[],
  onUpdate: (item: string[]) => void
}
> = ({ items, onUpdate }) => {
  const intl = useIntl();
  const classes = useStyles();

  const [editing, setEditing] = useState<string | null>(null);
  const [isNew, setIsNew] = useState<boolean>(false);


  const onDelete = (item: string) => {
    let newItems = [...items];
    newItems.splice(newItems.indexOf(item), 1);
    newItems = removeSimilarStrings(newItems);
    onUpdate(newItems);
  }

  const onEdit = (item: string) => {
    let newItems = [...items, item];
    editing && newItems.splice(newItems.indexOf(editing), 1);
    newItems = removeSimilarStrings(newItems);
    setEditing(null);
    setIsNew(false);
    onUpdate(newItems);
  }

  const columns: TableColumnDefinition<string>[] = [
    createTableColumn<string>({
      columnId: "domain",
      renderHeaderCell: () => intl.formatMessage({ defaultMessage: "Domains" }),
      renderCell: (data) => data
    }),
    createTableColumn<string>({
      columnId: "action",
      renderHeaderCell: () => intl.formatMessage({ defaultMessage: "Action" }),
      renderCell: (data) => <Elipsis item={data} />
    }),
  ]

  const Elipsis: React.FC<{ item: string; }> = ({ item }) => {
    return (
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button icon={<MoreHorizontalFilled />} appearance="transparent" />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<EditRegular />} onClick={() => { setEditing(item) }}>
              {intl.formatMessage({ defaultMessage: "Edit" })}
            </MenuItem>
            <MenuItem icon={<DeleteRegular />} onClick={() => { onDelete(item) }}>
              {intl.formatMessage({ defaultMessage: "Delete" })}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    )
  }

  return (
    <>
      {!!(editing || isNew) && <EditDialog
        open={!!(editing || isNew)}
        onClose={() => { setEditing(null); setIsNew(false) }}
        item={editing ?? ''}
        onUpdate={onEdit}
      />
      }
      {items.length > 0 &&
        <DataGrid items={items} columns={columns}>
          <DataGridHeader>
            <DataGridRow>
              {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<string>>
            {({ item, rowId }) => (
              <DataGridRow<string> key={rowId}>
                {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
              </DataGridRow>
            )}
          </DataGridBody>
        </DataGrid>
      }
      <Button appearance="transparent" onClick={() => { setIsNew(true) }} icon={<AddRegular />} className={classes.actions}>
        {intl.formatMessage({ defaultMessage: "Add domain" })}
      </Button>
    </>
  )
}

const EditDialog: React.FC<{
  open: boolean,
  onClose: () => void,
  item: string,
  onUpdate: (item: string) => void
}> = ({ open, onClose, item, onUpdate }) => {

  const intl = useIntl();
  const classes = useStyles();
  const [localItem, setLocalItem] = useState<string>(item);
  return (
    <Dialog open={open}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {intl.formatMessage({ defaultMessage: "Domain details" })}
          </DialogTitle>
          <DialogContent className={classes.container}>
            <Field label={intl.formatMessage({
              defaultMessage: 'Domain',
              description: 'Label for domain field'
            })}>
              <Input
                type="text"
                placeholder={'example.com'}
                value={localItem}
                onChange={(_, data) => setLocalItem(data.value)}
              />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onClose()}>
              {intl.formatMessage({ defaultMessage: "Cancel" })}
            </Button>
            <Button
              disabled={isEqual(item, localItem) || localItem === ""}
              appearance="primary"
              onClick={() => {
                onUpdate(localItem);
              }}
            >
              {intl.formatMessage({ defaultMessage: "Save" })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

